import React, { useState, Children } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, BookOpen, ShoppingCart, Lock, CheckCircle2 } from 'lucide-react';
import { Logo } from '../../Logo';
import { useCart } from '../../../contexts/CartContext';
import { usePrice } from '../../../contexts/PriceContext';
import { subscribeToNewsletter } from '../../../utils/firebase';
import { bookData, type Book } from './bookData';
const fadeUpVariants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.2, 0.65, 0.3, 0.9]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
const books: Book[] = bookData;
function BookCover({
  book,
  size = 'large'
}: {
  book: Book;
  size?: 'large' | 'small';
}) {
  const variantClasses: Record<Book['coverVariant'], string> = {
    charcoal: 'bg-charcoal',
    burgundy: 'bg-burgundy',
    navyslate: 'bg-navyslate'
  };
  const padding = size === 'large' ? 'p-8 md:p-10' : 'p-6';
  const titleSize = size === 'large' ? 'text-3xl md:text-4xl' : 'text-xl';
  const subtitleSize = size === 'large' ? 'text-xs' : 'text-[10px]';
  return <div className="relative w-full aspect-[3/4] group" data-id="element-1518">
      <div className={`absolute inset-0 ${variantClasses[book.coverVariant]} shadow-2xl ${book.coverImage ? '' : padding} flex flex-col border border-darkgray overflow-hidden transition-transform duration-500 group-hover:scale-[1.02]`} data-id="element-1519">
        {book.coverImage ? <img src={book.coverImage} alt={book.title} className="absolute inset-0 w-full h-full object-cover" data-id="element-1520" /> : <>
            {/* Top accent bar */}
            <div className="absolute top-0 left-0 w-full h-1.5 bg-tealslate" data-id="element-1521" />

            <Logo className={`${size === 'large' ? 'w-12 h-12' : 'w-8 h-8'} mb-auto opacity-80`} data-id="element-1522" />

            <div className="mt-auto" data-id="element-1523">
              <p className={`${subtitleSize} font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-3`} data-id="element-1524">
                Structured Nutrition
              </p>
              <h3 className={`${titleSize} font-serif text-white leading-tight mb-3`} data-id="element-1525">
                {book.title}
              </h3>
              <div className="w-8 h-0.5 bg-tealslate mb-3" data-id="element-1526" />
              <p className={`${subtitleSize} text-[#D8D8D0] uppercase tracking-widest`} data-id="element-1527">
                Jack Achenbach, MS, RDN
              </p>
            </div>
          </>}

        {/* Coming Soon Lock Overlay */}
        {book.status === 'coming-soon' && <div className="absolute inset-0 bg-charcoal/70 backdrop-blur-sm flex items-center justify-center" data-id="element-1528">
            <div className="text-center" data-id="element-1529">
              <Lock className="w-8 h-8 text-tealslate mx-auto mb-3" data-id="element-1530" />
              <p className="text-[10px] font-bold text-white uppercase tracking-[0.25em]" data-id="element-1531">
                Coming Soon
              </p>
            </div>
          </div>}
      </div>
      {/* Page edge depth */}
      <div className="absolute -right-2 top-2 bottom-2 w-2 bg-darkgray transform skew-y-[45deg] origin-top-left -z-10" data-id="element-1532" />
    </div>;
}
function formatCents(cents: number): string {
  return '$' + (cents / 100).toFixed(2);
}
type NewsletterStatus = 'idle' | 'submitting' | 'success' | 'error';
export function Books() {
  const featuredBook = books[0];
  const otherBooks = books.slice(1);
  const {
    addItem
  } = useCart();
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState<NewsletterStatus>('idle');
  const [newsletterError, setNewsletterError] = useState('');
  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setNewsletterStatus('submitting');
    setNewsletterError('');
    try {
      await subscribeToNewsletter(newsletterEmail, 'books_page');
      setNewsletterStatus('success');
      setNewsletterEmail('');
    } catch (err) {
      setNewsletterError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      setNewsletterStatus('error');
    }
  };
  const {
    cents,
    isLive,
    loading
  } = usePrice(featuredBook.lookupKey, featuredBook.priceCents);
  // Buyable if it has either a price ID or a lookup key
  const hasStripeRef = Boolean(featuredBook.stripePriceId || featuredBook.lookupKey);
  const canBuy = cents !== null && hasStripeRef;
  const handleAddFeatured = () => {
    if (!canBuy || cents === null) return;
    addItem({
      productId: featuredBook.id,
      name: featuredBook.title,
      unitPrice: cents,
      type: 'book',
      stripePriceId: featuredBook.stripePriceId,
      lookupKey: featuredBook.lookupKey
    });
  };
  return <div className="w-full min-h-screen bg-bodybg" data-id="element-1533">
      {/* HERO */}
      <section className="bg-bodybg pt-40 pb-20 px-6 border-b border-darkgray" data-id="element-1534">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1535">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="flex flex-col items-center" data-id="element-1536">
            <motion.p variants={fadeUpVariants} className="text-[10px] md:text-xs font-bold text-[#9A9A90] uppercase tracking-[0.25em] mb-6" data-id="element-1537">
              The Bookshop
            </motion.p>

            <motion.h1 variants={fadeUpVariants} className="text-4xl md:text-6xl font-serif text-white leading-tight mb-8" data-id="element-1538">
              Books for building structure.
            </motion.h1>

            <motion.div variants={fadeUpVariants} className="w-16 h-0.5 bg-tealslate mb-8" data-id="element-1539" />

            <motion.p variants={fadeUpVariants} className="text-base md:text-lg text-[#D8D8D0] max-w-2xl leading-relaxed" data-id="element-1540">
              Comprehensive guides distilled from years of clinical practice.
              Practical frameworks for nutrition, body composition, and
              sustainable metabolic health.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* FEATURED BOOK */}
      <section className="bg-[#F5F5F0] py-20 md:py-32 px-6" data-id="element-1541">
        <div className="max-w-7xl mx-auto" data-id="element-1542">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-1543">
            {/* Cover */}
            <motion.div variants={fadeUpVariants} className="mx-auto w-full max-w-md" data-id="element-1544">
              <BookCover book={featuredBook} size="large" data-id="element-1545" />
            </motion.div>

            {/* Pitch */}
            <motion.div variants={fadeUpVariants} data-id="element-1546">
              <div className="flex items-center gap-3 mb-4" data-id="element-1547">
                <span className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em]" data-id="element-1548">
                  Featured Release
                </span>
                {featuredBook.badge && <span className="text-[10px] font-bold text-white bg-tealslate px-3 py-1 uppercase tracking-wider" data-id="element-1549">
                    {featuredBook.badge}
                  </span>}
              </div>

              <h2 className="text-4xl md:text-5xl font-serif text-[#1F1F1F] mb-3 leading-tight" data-id="element-1550">
                {featuredBook.title}
              </h2>
              <p className="text-xl text-tealslate font-serif italic mb-6" data-id="element-1551">
                {featuredBook.subtitle}
              </p>
              <div className="w-12 h-0.5 bg-tealslate mb-6" data-id="element-1552" />

              <p className="text-lg text-[#3B3B3B] leading-relaxed mb-8" data-id="element-1553">
                {featuredBook.description}
              </p>

              {/* Meta info */}
              <div className="flex items-center gap-8 mb-10 pb-8 border-b border-[#D8D8D0]" data-id="element-1554">
                <div data-id="element-1555">
                  <p className="text-[10px] font-bold text-[#7A7A70] uppercase tracking-widest mb-1" data-id="element-1556">
                    Price
                  </p>
                  {cents === null ? <div className="h-8 w-24 bg-[#D8D8D0] animate-pulse rounded-sm" data-id="element-1557" /> : <p className="text-3xl font-serif text-[#1F1F1F]" data-id="element-1558">
                      {formatCents(cents)}
                      {!isLive && !loading && <span className="text-[10px] text-[#7A7A70] font-sans ml-2 normal-case tracking-normal" data-id="element-1559">
                          · est.
                        </span>}
                    </p>}
                </div>
                <div data-id="element-1560">
                  <p className="text-[10px] font-bold text-[#7A7A70] uppercase tracking-widest mb-1" data-id="element-1561">
                    Format
                  </p>
                  <p className="text-base text-[#3B3B3B]" data-id="element-1562">
                    {featuredBook.pages} · PDF
                  </p>
                </div>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-1563">
                <button onClick={handleAddFeatured} disabled={!canBuy} className="flex items-center justify-center gap-2 px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-navyslate transition-colors disabled:opacity-60 disabled:cursor-not-allowed" title={!canBuy ? 'Not yet available for purchase' : undefined} data-id="element-1564">
                  <ShoppingCart className="w-4 h-4" data-id="element-1565" />
                  {cents !== null ? 'Add to Cart — ' + formatCents(cents) : 'Add to Cart'}
                </button>
                <Link to="/booklet?preview=1" className="flex items-center justify-center gap-2 px-8 py-4 border border-[#1F1F1F] text-[#1F1F1F] text-sm font-bold uppercase tracking-wider hover:bg-[#1F1F1F] hover:text-white transition-colors" data-id="element-1566">
                  <BookOpen className="w-4 h-4" data-id="element-1567" />
                  Preview Online
                </Link>
              </div>

              <p className="text-xs text-[#7A7A70] italic" data-id="element-1568">
                Instant download · 30-day satisfaction guarantee
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* NEWSLETTER CTA */}
      <section className="bg-[#F5F5F0] py-20 md:py-24 px-6" data-id="element-1569">
        <div className="max-w-3xl mx-auto text-center" data-id="element-1570">
          <p className="text-[10px] font-bold text-tealslate uppercase tracking-[0.25em] mb-4" data-id="element-1571">
            Stay In The Loop
          </p>
          <h2 className="text-3xl md:text-4xl font-serif text-[#1F1F1F] mb-6" data-id="element-1572">
            Be the first to know when new books drop.
          </h2>
          <p className="text-base text-[#3B3B3B] mb-10 leading-relaxed" data-id="element-1573">
            Join the newsletter for early access, launch discounts, and
            exclusive content.
          </p>

          {newsletterStatus === 'success' ? <div className="max-w-xl mx-auto bg-white border-l-4 border-tealslate p-6 flex items-start gap-4 text-left" data-id="element-1574">
              <CheckCircle2 className="w-6 h-6 text-tealslate shrink-0 mt-0.5" data-id="element-1575" />
              <div data-id="element-1576">
                <p className="font-serif text-lg text-[#1F1F1F] mb-1" data-id="element-1577">
                  You're on the list.
                </p>
                <p className="text-sm text-[#3B3B3B]" data-id="element-1578">
                  We'll send you a note the moment new books drop.
                </p>
              </div>
            </div> : <form className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto" onSubmit={handleNewsletterSubmit} data-id="element-1579">
              <input type="email" placeholder="Enter your email" value={newsletterEmail} onChange={e => setNewsletterEmail(e.target.value)} className="flex-1 bg-white border border-[#D8D8D0] px-6 py-4 text-[#1F1F1F] focus:outline-none focus:border-tealslate transition-colors" required disabled={newsletterStatus === 'submitting'} data-id="element-1580" />
              <button type="submit" disabled={newsletterStatus === 'submitting'} className="px-8 py-4 bg-tealslate text-white text-sm font-bold uppercase tracking-wider hover:bg-navyslate transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-1581">
                {newsletterStatus === 'submitting' ? <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1582" />
                    Subscribing…
                  </> : <>
                    Subscribe <ArrowRight className="w-4 h-4" data-id="element-1583" />
                  </>}
              </button>
            </form>}

          {newsletterStatus === 'error' && <p className="text-sm text-red-700 mt-4" data-id="element-1584">{newsletterError}</p>}
        </div>
      </section>
    </div>;
}